import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useEffect, useState, type ReactNode } from 'react';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { dropdownAnimationVariants } from '~/utils/animationVariants';
import { classNames } from '~/utils/classNames';

interface AssistantActionButtonsProps {
  children: ReactNode;
}

export const AssistantActionButtons = memo<AssistantActionButtonsProps>(({ children }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setOpen(false);
    };

    if (open) {
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
      };
    }

    return undefined;
  }, [open]);

  return (
    <DropdownMenu.Root open={open} modal={false} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <LegacyIconButton icon="i-ph:dots-three-outline-fill" className="focus:outline-none" size="sm" />
      </DropdownMenu.Trigger>
      <AnimatePresence>
        {open && (
          <DropdownMenu.Portal forceMount>
            <DropdownMenu.Content
              className="z-visibility-dropdown"
              asChild
              forceMount
              avoidCollisions={false}
              sideOffset={3}
              align="end"
              onFocusOutside={() => setOpen(false)}
            >
              <motion.div initial="close" animate="open" exit="close" variants={dropdownAnimationVariants}>
                <div className="bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 p-1 border border-bolt-elements-borderColor rounded-md text-sm select-none flex flex-col gap-1">
                  {children}
                </div>
              </motion.div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        )}
      </AnimatePresence>
    </DropdownMenu.Root>
  );
});

interface AssistantActionButtonProps {
  className?: string;
  disabled?: boolean;
  children: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void | 'close';
}

export const AssistantActionButton = memo<AssistantActionButtonProps>(({ className, disabled, onClick, children }) => {
  return (
    <DropdownMenu.Item asChild disabled={disabled}>
      <button
        className={classNames(
          'flex gap-1.5 items-center px-2 py-1.5 rounded-md bg-transparent enabled:hover:bg-bolt-ds-surfaceHighlight text-xs min-h-[30px] disabled:cursor-not-allowed disabled:opacity-50 w-full text-left focus:outline-none',
          className,
        )}
        disabled={disabled}
        onClick={onClick}
      >
        {children}
      </button>
    </DropdownMenu.Item>
  );
});
